/**
 * CompletionScreen — Step 8
 *
 * Two-column layout: Setup Summary (left) + What to Do Next (right).
 * Status logic:
 *   Step 1 Company Setup:       green if ≥1 company; grey otherwise
 *   Step 2 User Access:         green/amber/grey from stepStatuses
 *   Step 3 Timesheet Settings:  green if settings.updatedBy != null; grey otherwise
 *   Step 4 Policy Rules:        green/amber/grey from stepStatuses
 *   Step 5 Notifications:       green/amber/grey from stepStatuses
 *   Step 6 Approval Workflows:  green if ≥1 workflow; amber if skipped; grey if not visited
 *   Step 7 Overtime Types:      green if ≥1 overtime type; amber if skipped; grey if not visited
 */

import { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { apiClient } from '@/services/apiClient';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import type { OrganisationTimesheetSettingsDto } from '@/types/api';
import type { ApprovalWorkflow } from '@/types/workflow';
import type { OvertimeType } from '@/types/timesheetSettings';
import type { CompanyListItem } from '@/types';

const PRIMARY = '#00796B';

type StepStatus = 'completed' | 'skipped' | 'not-configured';

interface CompletionScreenProps {
  stepStatuses: {
    step2: StepStatus;
    step3: StepStatus;
    step4: StepStatus;
    step5: StepStatus;
    step6: StepStatus;
    step7: StepStatus;
  };
}

function StatusLabel({ status }: { status: StepStatus }) {
  if (status === 'completed') {
    return <span className="text-xs font-medium text-green-600">✓ Completed</span>;
  }
  if (status === 'skipped') {
    return <span className="text-xs font-medium text-amber-500">⚠ Skipped</span>;
  }
  return <span className="text-xs font-medium text-muted-foreground">— Not configured</span>;
}

export function CompletionScreen({ stepStatuses }: CompletionScreenProps) {
  const { completeOnboarding } = useOnboarding();
  const { currentCompany } = useCompany();
  const navigate = useNavigate();

  const [companyCount, setCompanyCount] = useState<number | null>(null);
  const [timesheetSettingsUpdatedBy, setTimesheetSettingsUpdatedBy] = useState<
    string | null | undefined
  >(undefined);
  const [workflowCount, setWorkflowCount] = useState<number | null>(null);
  const [overtimeTypeCount, setOvertimeTypeCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      try {
        const [companies, settings] = await Promise.allSettled([
          apiClient.get<CompanyListItem[]>('/companies'),
          apiClient.get<OrganisationTimesheetSettingsDto>('/settings/timesheet'),
        ]);

        if (!cancelled) {
          setCompanyCount(
            companies.status === 'fulfilled' ? companies.value.length : 0,
          );
          setTimesheetSettingsUpdatedBy(
            settings.status === 'fulfilled' ? settings.value.updatedBy : null,
          );
        }

        const workflowsResult = await Promise.allSettled([
          apiClient.get<ApprovalWorkflow[]>('/workflows'),
        ]);
        if (!cancelled) {
          setWorkflowCount(
            workflowsResult[0].status === 'fulfilled' ? workflowsResult[0].value.length : 0,
          );
        }

        if (currentCompany?.id) {
          const otResult = await Promise.allSettled([
            apiClient.get<OvertimeType[]>(`/companies/${currentCompany.id}/overtime-types`),
          ]);
          if (!cancelled) {
            setOvertimeTypeCount(
              otResult[0].status === 'fulfilled' ? otResult[0].value.length : 0,
            );
          }
        } else {
          if (!cancelled) setOvertimeTypeCount(0);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [currentCompany?.id]);

  // Derived statuses
  const step1Status: StepStatus =
    companyCount === null ? 'not-configured' : companyCount >= 1 ? 'completed' : 'not-configured';

  const step3Status: StepStatus =
    timesheetSettingsUpdatedBy == null ? 'not-configured' : 'completed';

  const step6Status: StepStatus =
    stepStatuses.step6 === 'skipped'
      ? 'skipped'
      : workflowCount != null && workflowCount >= 1
      ? 'completed'
      : 'not-configured';

  const step7Status: StepStatus =
    stepStatuses.step7 === 'skipped'
      ? 'skipped'
      : overtimeTypeCount != null && overtimeTypeCount >= 1
      ? 'completed'
      : 'not-configured';

  const handleGoToDashboard = () => {
    completeOnboarding();
    navigate('/');
  };

  const handleViewSettings = () => {
    completeOnboarding();
    navigate('/settings/general');
  };

  const handleEdit = (path: string) => {
    completeOnboarding();
    navigate(path);
  };

  const summaryRows: {
    label: string;
    status: StepStatus;
    editPath: string;
  }[] = [
    { label: 'Company Setup',       status: step1Status,              editPath: '/companies' },
    { label: 'User Access',         status: stepStatuses.step2,       editPath: '/settings/general' },
    { label: 'Timesheet Settings',  status: step3Status,              editPath: '/settings/general' },
    { label: 'Policy Rules',        status: stepStatuses.step4,       editPath: '/settings/general' },
    { label: 'Notifications',       status: stepStatuses.step5,       editPath: '/settings/notifications' },
    { label: 'Approval Workflows',  status: step6Status,              editPath: '/settings/approvals' },
    { label: 'Overtime Types',      status: step7Status,              editPath: '/companies' },
  ];

  return (
    <div className="flex flex-col items-center w-full h-full overflow-y-auto py-8 px-6">
      <div className="w-full max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ backgroundColor: `${PRIMARY}18` }}
          >
            <CheckCircle className="w-9 h-9" style={{ color: PRIMARY }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h1>
          <p className="text-gray-500 text-sm">
            Time is configured and ready for your team.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* LEFT: Setup Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Setup Summary</h3>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <div key={i} className="h-5 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {summaryRows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">{row.label}</div>
                    <div className="flex items-center gap-3">
                      <StatusLabel status={row.status} />
                      <button
                        className="text-xs underline text-muted-foreground hover:text-gray-700"
                        onClick={() => handleEdit(row.editPath)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: What to Do Next */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">What to Do Next</h3>
            <ol className="space-y-3">
              {[
                'Add your first employee and assign them to a company',
                'Assign an approval workflow to a team or department',
                'Ask your team to submit their first timesheet',
                'Review pending approvals in the Approvals dashboard',
                "Run a utilisation report to see your team's billable hours",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="shrink-0 w-5 h-5 rounded-full text-xs font-semibold flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: `${PRIMARY}18`, color: PRIMARY }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-sm text-gray-600 leading-snug">{item}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col items-center gap-3">
          <Button
            size="lg"
            className="px-8 font-semibold"
            style={{ backgroundColor: PRIMARY, color: 'white' }}
            onClick={handleGoToDashboard}
          >
            Go to Dashboard →
          </Button>
          <button
            className="text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            onClick={handleViewSettings}
          >
            View Settings
          </button>
        </div>
      </div>
    </div>
  );
}
